import { put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';

import { ApiResult, SearchDTO, SearchVM } from 'common/models';
import {
    CATEGORY_EDIT_SUCCESS,
    EDIT_SUCCESS_MESSAGE,
    FAILURE_HEADER,
    INDICATOR_EDIT_SUCCESS,
    SUB_CATEGORY_EDIT_SUCCESS,
    SUCCESS_HEADER,
    CATEGORY_ADD_SUCCESS,
    INDICATOR_ADD_SUCCESS,
    INDICATOR_COPY_SUCCESS,
    ARCHIVE_FAILED,
    ARCHIVE_SUCCESS,
    CLONE_SUCCESS,
    INDICATOR_ADD_FAIL,
    INDICATOR_COPY_FAIL,
    INDICATOR_LINK_SUCCESS,
    SUB_CATEGORY_ADD_SUCCESS,
    DELETE_POPUP_MSG,
    DELETE_POPUP_SUCCESS,
    MASTER_DATA_SAVED,
    MOVED_INDICATOR_SUCCESSFULLY_MESSAGE,
    MOVED_INDICATOR_SUCCESSFULLY_TITLE,
    MOVED_SUBCATEGORY_SUCCESSFULLY_MESSAGE
} from 'common/config';
import { appStateActions } from 'common/redux/app-state';
import { EntityType, MessageType, Schema } from 'common/enums';
import { PagedRequest } from 'common/models/data/pagedRequest.model';
import { PaginatedApiResponse } from 'common/models/api-service/api-response.model';
import { CategoryDTO, Entity, EntityVM, MasterDataEntity } from 'common/models/data/entity.model';
import { IErrorApiResponse } from 'common/models/api-service/error-response.model';
import { findCategories, findCategoriesByHierarchyId } from 'common/utils/table';
import { hasValue } from 'common/utils/data';
import { IndicatorUpdateDTO } from 'common/models/data/indicator.model';

import {
    EditSubCategoryDTO,
    LinkIndicatorDTO,
    SubCategoryDTO
} from 'modules/master-data/models/master-data.model';

import { MasterDataService } from './masterData.service';
import { masterDataActions } from './masterData.slice';
import { getHierarchicalDataSelector, getMasterDataValue } from './masterData.selector';
import { fileUploadActions } from 'common/redux/file-upload';
import {
    CLONE_FAILED,
    DEFAULT_ERROR_MESSAGE,
    DISCARD_CHANGES_SUCCESS,
    EXPORT_POPUP_ERROR_MESSAGE,
    EXPORT_SUCCESS,
    MAP_TO_PUBLIC_SUCCESS,
    MOVE_FAILED
} from 'common/config/message.config';
import { ExtractType } from 'common/enums/data.enum';

const getMasterData = function* ({ payload }: PayloadAction<PagedRequest>) {
    const categories:
        | ApiResult<PaginatedApiResponse<MasterDataEntity[]> | IErrorApiResponse>
        | undefined = yield MasterDataService.getCategories(payload);
    if (categories && categories.hasErrors && categories.errors) {
        yield put({
            type: masterDataActions.getMasterDataError.type,
            payload: categories.errors
        });
    } else {
        yield put({
            type: masterDataActions.getMasterDataSuccess.type,
            payload: categories?.value
        });
    }
};

const getMasterDataWatch = function* () {
    yield takeLatest(masterDataActions.getMasterData.type, getMasterData);
};

const getEntitiesByCategory = function* ({ payload }: PayloadAction<EntityVM>) {
    const masterData: Entity[] = yield select(getMasterDataValue);
    let subCats: any = {};
    if (payload.entityType === EntityType.Category) {
        subCats = findCategories(masterData, payload.id);
    } else {
        subCats = findCategoriesByHierarchyId(masterData, payload.hierarchyId);
    }
    if (!payload.isExpanded && !hasValue(subCats.subCategories)) {
        yield put({
            type: appStateActions.showLoader.type,
            payload: true
        });
        let categories: ApiResult<IErrorApiResponse | EntityVM[]> = {
            errors: [],
            hasErrors: false
        };
        if (payload.entityType === EntityType.Category)
            categories = yield MasterDataService.getEntitiesByCategoryId(payload.id);
        else categories = yield MasterDataService.getEntitiesByHierarchyId(payload.hierarchyId);

        if (categories && categories.hasErrors && categories.errors) {
            yield put({
                type: masterDataActions.toggleExpansionFailed.type,
                payload: categories.errors
            });
        } else {
            yield put({
                type: masterDataActions.toggleExpansionSuccess.type,
                payload: {
                    parent: payload,
                    subCategories: categories?.value
                }
            });
        }
    } else {
        yield put({
            type: appStateActions.showLoader.type,
            payload: false
        });
    }
};

const getEntitiesByCategoryWatch = function* () {
    yield takeLatest(masterDataActions.toggleExpansion.type, getEntitiesByCategory);
};

const refreshEntitiesByCategory = function* ({
    payload
}: PayloadAction<{ entityType: EntityType; entityId: string }>) {
    let categories: ApiResult<IErrorApiResponse | Entity[]> = {
        errors: [],
        hasErrors: false
    };
    if (payload.entityType === EntityType.Category)
        categories = yield MasterDataService.getEntitiesByCategoryId(payload.entityId);
    else categories = yield MasterDataService.getEntitiesByHierarchyId(payload.entityId);
    if (categories && categories.hasErrors && categories.errors) {
        yield put({
            type: masterDataActions.refreshEntitiesByCategoryFailed.type,
            payload: categories.errors
        });
    } else {
        yield put({
            type: masterDataActions.refreshEntitiesByCategorySuccess.type,
            payload: {
                parent: payload,
                subCategories: categories?.value
            }
        });
    }
};

const refreshEntitiesByCategoryWatch = function* () {
    yield takeEvery(masterDataActions.refreshEntitiesByCategory.type, refreshEntitiesByCategory);
};

const getHierarchicalData = function* ({ payload }: PayloadAction<PagedRequest>) {
    const categories: ApiResult<PaginatedApiResponse<Entity[]> | IErrorApiResponse> | undefined =
        yield MasterDataService.getCategories(payload);
    if (categories && categories.hasErrors && categories.errors) {
        yield put({
            type: masterDataActions.getHierarchicalDataError.type,
            payload: categories.errors
        });
    } else {
        yield put({
            type: masterDataActions.getHierarchicalDataSuccess.type,
            payload: categories?.value
        });
    }
};

const getHierarchicalDataWatch = function* () {
    yield takeLatest(masterDataActions.getHierarchicalData.type, getHierarchicalData);
};

const getHierarchicalDataByCategory = function* ({ payload }: PayloadAction<EntityVM>) {
    const masterData: Entity[] = yield select(getHierarchicalDataSelector);
    const subCats = findCategories(masterData, payload.id);
    if (!payload.isExpanded && !hasValue(subCats.subCategories)) {
        yield put({
            type: appStateActions.showLoader.type,
            payload: true
        });
        let categories: ApiResult<IErrorApiResponse | Entity[]> = {
            errors: [],
            hasErrors: false
        };
        if (payload.entityType === EntityType.Category)
            categories = yield MasterDataService.getEntitiesByCategoryId(payload.id);
        else categories = yield MasterDataService.getEntitiesByHierarchyId(payload.hierarchyId);
        if (categories && categories.hasErrors && categories.errors) {
            yield put({
                type: masterDataActions.toggleHierarchicalDataExpansionFailed.type,
                payload: categories.errors
            });
        } else {
            yield put({
                type: masterDataActions.toggleHierarchicalDataExpansionSuccess.type,
                payload: {
                    parent: payload,
                    subCategories: categories?.value
                }
            });
        }
    } else {
        yield put({
            type: appStateActions.showLoader.type,
            payload: false
        });
    }
};

const getHierarchicalDataByCategoryWatch = function* () {
    yield takeLatest(
        masterDataActions.toggleHierarchicalDataExpansion.type,
        getHierarchicalDataByCategory
    );
};

const updateCategory = function* ({ payload }: PayloadAction<CategoryDTO>) {
    const { hasErrors, errors, value } = yield MasterDataService.updateCategory(payload);
    if (hasErrors && errors) {
        yield put({
            type: masterDataActions.updateCategoryFailed.type,
            payload: value && value.detail
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value && value.detail,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: masterDataActions.updateCategorySuccess.type,
            payload: payload
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: CATEGORY_EDIT_SUCCESS,
                type: MessageType.Success
            }
        });
        yield put({
            type: masterDataActions.getMasterData.type,
            payload: { pageNumber: 1, pageSize: 20, searchText: '' }
        });
    }
};

const updateCategoryWatch = function* () {
    yield takeLatest(masterDataActions.updateCategory.type, updateCategory);
};

const addSubCategory = function* ({ payload }: PayloadAction<SubCategoryDTO>) {
    const { hasErrors, errors, value } = yield MasterDataService.addSubCategory(payload);
    if (hasErrors && errors) {
        yield put({
            type: masterDataActions.addSubCategoryFailed.type,
            payload: value && value.detail
        });

        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value && value.detail,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: appStateActions.showLoader,
            payload: false
        });
        yield put({
            type: masterDataActions.addSubCategorySuccess.type,
            payload: EDIT_SUCCESS_MESSAGE
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: SUB_CATEGORY_ADD_SUCCESS,
                type: MessageType.Success
            }
        });
        yield put({
            type: masterDataActions.refreshEntitiesByCategory.type,
            payload: {
                entityType: payload.parentEntityType,
                entityId:
                    payload.parentEntityType === EntityType.Category
                        ? payload.categoryId
                        : payload.parentHierarchyId
            }
        });
    }
};

const addSubCategoryWatch = function* () {
    yield takeLatest(masterDataActions.addSubCategory.type, addSubCategory);
};

const updateSubCategory = function* ({ payload }: PayloadAction<EditSubCategoryDTO>) {
    const { hasErrors, errors, value } = yield MasterDataService.updateSubCategory(payload);
    if (hasErrors && errors) {
        yield put({
            type: masterDataActions.updateSubCategoryFailed.type,
            payload: value && value.detail
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value && value.detail,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: masterDataActions.updateSubCategorySuccess.type,
            payload: payload
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: SUB_CATEGORY_EDIT_SUCCESS,
                type: MessageType.Success
            }
        });
        yield put({
            type: masterDataActions.refreshEntitiesByCategory.type,
            payload: {
                entityType:
                    payload.parentSubCategoryId != null
                        ? EntityType.SubCategory
                        : EntityType.Category,
                entityId:
                    payload.parentSubCategoryId != null
                        ? payload.parentHierarchyId
                        : payload.categoryId
            }
        });
    }
};

const updateSubCategoryWatch = function* () {
    yield takeLatest(masterDataActions.updateSubCategory.type, updateSubCategory);
};

const fetchSubCategory = function* ({ payload }: PayloadAction<string>) {
    const { hasErrors, errors, value }: ApiResult<SubCategoryDTO | IErrorApiResponse> =
        yield MasterDataService.fetchSubCategory(payload);
    if (hasErrors && errors) {
        const error: string = errors[0];
        yield put({
            type: masterDataActions.fetchSubCategoryFailed.type,
            payload: error
        });
    } else {
        yield put({
            type: masterDataActions.fetchSubCategorySuccess.type,
            payload: value
        });
    }
};

const fetchSubCategoryWatch = function* () {
    yield takeLatest(masterDataActions.fetchSubCategory.type, fetchSubCategory);
};

const updateIndicator = function* ({
    payload
}: PayloadAction<{ editedIndicator: IndicatorUpdateDTO; indicator: EntityVM }>) {
    const { hasErrors, errors, value } = yield MasterDataService.updateIndicator(
        payload.editedIndicator
    );
    if (hasErrors && errors) {
        yield put({
            type: masterDataActions.updateIndicatorFailed.type,
            payload: value && value.detail
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value && value.detail,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: masterDataActions.updateIndicatorSuccess.type,
            payload: payload.editedIndicator
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: INDICATOR_EDIT_SUCCESS,
                type: MessageType.Success
            }
        });
        yield put({
            type: masterDataActions.refreshEntitiesByCategory.type,
            payload: {
                entityType: EntityType.SubCategory,
                entityId: payload.indicator.hierarchyId
            }
        });
    }
};

const updateIndicatorWatch = function* () {
    yield takeLatest(masterDataActions.updateIndicator.type, updateIndicator);
};

const deleteData = function* ({
    payload
}: PayloadAction<{
    id: string;
    entityType: EntityType;
    parentHierarchyId: string;
    categoryId: string;
    hierarchyId: string;
}>) {
    const { hasErrors, errors, value }: ApiResult<any> = yield MasterDataService.deleteData(
        payload.id,
        payload.entityType,
        payload.hierarchyId
    );
    if (hasErrors && errors) {
        yield put({
            type: masterDataActions.deleteDataError.type,
            payload: value && value.detail
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value && value.detail,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: masterDataActions.deleteDataSuccess.type,
            payload
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: DELETE_POPUP_SUCCESS,
                message: DELETE_POPUP_MSG,
                type: MessageType.Success
            }
        });
        if (payload.entityType !== EntityType.Category) {
            yield put({
                type: masterDataActions.refreshEntitiesByCategory.type,
                payload: {
                    entityType: payload.parentHierarchyId
                        ? EntityType.SubCategory
                        : EntityType.Category,
                    entityId: payload.parentHierarchyId
                        ? payload.parentHierarchyId
                        : payload.categoryId
                }
            });
        } else {
            yield put({
                type: masterDataActions.deleteCategoryData.type,
                payload: payload.id
            });
        }
    }
};

const deleteDataWatch = function* () {
    yield takeLatest(masterDataActions.deleteData.type, deleteData);
};

const getSearchList = function* ({ payload }: PayloadAction<SearchDTO>) {
    const apiResult: ApiResult<PaginatedApiResponse<SearchVM[]>> | undefined =
        yield MasterDataService.getSearchList(payload);
    if (apiResult) {
        if (apiResult.hasErrors) {
            yield put({
                type: masterDataActions.searchListError.type
            });
        } else {
            yield put({
                type: masterDataActions.searchListSuccess.type,
                payload: apiResult.value
            });
        }
    } else {
        yield put({
            type: masterDataActions.searchListError.type
        });
    }
};

const getSearchListWatch = function* () {
    yield takeLatest(masterDataActions.searchList.type, getSearchList);
};

const addCategory = function* ({ payload }: PayloadAction<CategoryDTO>) {
    const { hasErrors, errors, value } = yield MasterDataService.addCategory(payload);
    if (hasErrors && errors) {
        yield put({
            type: masterDataActions.addCategoryFailed.type,
            payload: value && value.detail
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value && value.detail,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: masterDataActions.addCategorySuccess.type,
            payload: value
        });
        yield put({
            type: appStateActions.showLoader,
            payload: false
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: CATEGORY_ADD_SUCCESS,
                type: MessageType.Success
            }
        });
        yield put({
            type: masterDataActions.getMasterData.type,
            payload: { pageNumber: 1, pageSize: 20, searchText: '' }
        });
        yield put({
            type: masterDataActions.setScrollToTop.type,
            payload: true
        });
    }
};

const addCategoryWatch = function* () {
    yield takeLatest(masterDataActions.addCategory.type, addCategory);
};

const saveToMaster = function* ({ payload }: PayloadAction<any>) {
    const { hasErrors, errors, value }: ApiResult<any> = yield MasterDataService.saveToMaster();
    if (hasErrors && errors) {
        yield put({
            type: masterDataActions.saveToMasterFailed.type,
            payload: value && value.detail
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value && value.detail,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: masterDataActions.saveToMasterSuccess.type,
            payload
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: MASTER_DATA_SAVED,
                type: MessageType.Success
            }
        });
    }
};

const saveToMasterWatch = function* () {
    yield takeLatest(masterDataActions.saveToMaster.type, saveToMaster);
};

const applyChanges = function* ({ payload }: PayloadAction<any>) {
    const { hasErrors, errors, value } = yield MasterDataService.loadToReviewSchema(payload);
    if (hasErrors && errors) {
        yield put({
            type: masterDataActions.applyChangesFailed.type
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value && value.detail,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: masterDataActions.applyChangesSuccess.type
        });
        yield put({
            type: masterDataActions.getMasterData.type,
            payload: { pageNumber: 1, pageSize: 20, searchText: '' }
        });
        if (payload.sourceSchema === Schema.Master && payload.targetSchema === Schema.GBB) {
            yield put({
                type: appStateActions.showMessageModal.type,
                payload: {
                    isVisible: true,
                    header: SUCCESS_HEADER,
                    message: MAP_TO_PUBLIC_SUCCESS,
                    type: MessageType.Success
                }
            });
        }
        yield put({
            type: fileUploadActions.getSyncDetails.type
        });
        yield put({
            type: masterDataActions.fetchReviewStatus.type
        });
    }
};

const applyChangesWatch = function* () {
    yield takeLatest(masterDataActions.applyChanges.type, applyChanges);
};

const addIndicator = function* ({ payload }: PayloadAction<any>) {
    const { hasErrors, errors, value }: ApiResult<any> = yield MasterDataService.addIndicator(
        payload
    );
    if (hasErrors && errors) {
        yield put({
            type: masterDataActions.addIndicatorFailed.type,
            payload: { message: value ? value.detail : INDICATOR_ADD_FAIL }
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value ? value.detail : INDICATOR_ADD_FAIL,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: masterDataActions.addIndicatorSuccess.type,
            payload: payload
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: INDICATOR_ADD_SUCCESS,
                type: MessageType.Success
            }
        });
    }
};

const addIndicatorWatch = function* () {
    yield takeLatest(masterDataActions.addIndicator.type, addIndicator);
};

const linkIndicator = function* ({ payload }: PayloadAction<LinkIndicatorDTO>) {
    const { hasErrors, errors, value } = yield MasterDataService.linkIndicator(payload);
    if (hasErrors && errors) {
        const error: string = errors[0];
        yield put({
            type: masterDataActions.linkIndicatorFailed.type,
            payload: { message: error }
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value && value.detail,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: masterDataActions.linkIndicatorSuccess.type,
            payload: payload
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: INDICATOR_LINK_SUCCESS,
                type: MessageType.Success
            }
        });
        yield put({
            type: masterDataActions.refreshEntitiesByCategory.type,
            payload: {
                entityType: EntityType.SubCategory,
                entityId: payload.targetHierarchyId
            }
        });
        yield put({
            type: masterDataActions.refreshEntitiesByCategory.type,
            payload: {
                entityType: EntityType.SubCategory,
                entityId: payload.sourceHierarchyId
            }
        });
    }
};

const linkIndicatorWatch = function* () {
    yield takeLatest(masterDataActions.linkIndicator.type, linkIndicator);
};

const archive = function* ({
    payload
}: PayloadAction<{
    id: string;
    entityType: EntityType;
    parentHierarchyId: string;
    categoryId: string;
    hierarchyId: string;
}>) {
    const { hasErrors, errors, value } = yield MasterDataService.archive(
        payload.id,
        payload.entityType,
        payload.hierarchyId
    );
    if (hasErrors && errors) {
        yield put({
            type: masterDataActions.archiveFailed.type
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: ARCHIVE_FAILED,
                message: value && value.detail,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: masterDataActions.archiveSuccess.type,
            payload: payload.id
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: ARCHIVE_SUCCESS,
                type: MessageType.Success
            }
        });
        if (payload.entityType !== EntityType.Category) {
            yield put({
                type: masterDataActions.refreshEntitiesByCategory.type,
                payload: {
                    entityType: payload.parentHierarchyId
                        ? EntityType.SubCategory
                        : EntityType.Category,
                    entityId: payload.parentHierarchyId
                        ? payload.parentHierarchyId
                        : payload.categoryId
                }
            });
        }
    }
};

const archiveWatch = function* () {
    yield takeLatest(masterDataActions.archive.type, archive);
};

const cloneEntity = function* ({ payload }: PayloadAction<any>) {
    const { hasErrors, errors, value }: ApiResult<any> = yield MasterDataService.cloneEntity(
        payload
    );
    if (hasErrors && errors) {
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value?.detail || CLONE_FAILED,
                type: MessageType.Error
            }
        });
        yield put({
            type: masterDataActions.cloneEntityFailed.type,
            payload: value
        });
    } else {
        yield put({
            type: masterDataActions.cloneEntitySuccess.type,
            payload: value
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: CLONE_SUCCESS,
                type: MessageType.Success
            }
        });
        yield put({
            type: masterDataActions.getMasterData.type,
            payload: { pageNumber: 1, pageSize: 20, searchText: '' }
        });
    }
};

const cloneEntityWatch = function* () {
    yield takeLatest(masterDataActions.cloneEntity.type, cloneEntity);
};

const fetchReviewStatus = function* () {
    const { hasErrors, errors, value }: ApiResult<boolean | IErrorApiResponse> =
        yield MasterDataService.getReviewStatusUrl();
    if (hasErrors && errors) {
        const error: string = errors[0];
        yield put({
            type: masterDataActions.fetchReviewStatusFailed.type,
            payload: error
        });
    } else {
        yield put({
            type: masterDataActions.fetchReviewStatusSuccess.type,
            payload: value
        });
    }
};

const fetchReviewStatusWatch = function* () {
    yield takeLatest(
        [
            masterDataActions.fetchReviewStatus.type,
            masterDataActions.addCategorySuccess.type,
            masterDataActions.addSubCategorySuccess.type,
            masterDataActions.addIndicatorSuccess.type,
            masterDataActions.updateCategorySuccess.type,
            masterDataActions.updateSubCategorySuccess.type,
            masterDataActions.updateIndicatorSuccess.type,
            masterDataActions.deleteDataSuccess.type,
            masterDataActions.archiveSuccess.type,
            masterDataActions.copyIndicatorSuccess.type,
            masterDataActions.moveSubCategorySuccess.type,
            masterDataActions.moveIndicatorSuccess.type,
            masterDataActions.linkIndicatorSuccess.type,
            masterDataActions.discardChangesSuccess.type,
            masterDataActions.copyIndicatorSuccess.type,
            masterDataActions.cloneEntitySuccess.type
        ],
        fetchReviewStatus
    );
};

const moveSubCategory = function* ({ payload }: PayloadAction<any>) {
    const { hasErrors, errors, value }: ApiResult<any> = yield MasterDataService.moveSubCategory(
        payload
    );
    if (hasErrors && errors) {
        const error: string = errors[0];
        yield put({
            type: masterDataActions.moveSubCategoryError.type,
            payload: { message: error }
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value ? value.detail : MOVE_FAILED,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: masterDataActions.moveSubCategorySuccess.type,
            payload: value
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: MOVED_SUBCATEGORY_SUCCESSFULLY_MESSAGE,
                type: MessageType.Success
            }
        });
        yield put({
            type: masterDataActions.getMasterData.type,
            payload: { pageNumber: 1, pageSize: 20, searchText: '' }
        });
    }
};

const moveSubCategoryWatch = function* () {
    yield takeLatest(masterDataActions.moveSubCategoryBegin.type, moveSubCategory);
};

const copyIndicator = function* ({ payload }: PayloadAction<any>) {
    const { hasErrors, errors, value }: ApiResult<any> = yield MasterDataService.addIndicator(
        payload
    );
    if (hasErrors && errors) {
        const error: string = errors[0];
        yield put({
            type: masterDataActions.copyIndicatorFailed.type,
            payload: { message: error }
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value?.detail || INDICATOR_COPY_FAIL,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: masterDataActions.copyIndicatorSuccess.type,
            payload: payload
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: INDICATOR_COPY_SUCCESS,
                type: MessageType.Success
            }
        });
        yield put({
            type: masterDataActions.getMasterData.type,
            payload: { pageNumber: 1, pageSize: 20, searchText: '' }
        });
    }
};

const copyIndicatorWatch = function* () {
    yield takeLatest(masterDataActions.copyIndicator.type, copyIndicator);
};

const resetMasterDataState = function* () {
    yield put({
        type: masterDataActions.resetState.type
    });
};

const resetMasterDataStateWatch = function* () {
    yield takeLatest([masterDataActions.updateSubCategorySuccess.type], resetMasterDataState);
};

const moveIndicator = function* ({ payload }: PayloadAction<any>) {
    const { hasErrors, errors, value }: ApiResult<any> = yield MasterDataService.moveIndicator(
        payload
    );
    if (hasErrors && errors) {
        const error: string = errors[0];
        yield put({
            type: masterDataActions.moveIndicatorError.type,
            payload: { message: error }
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value?.detail || MOVE_FAILED,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: masterDataActions.moveIndicatorSuccess.type,
            payload: value
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: MOVED_INDICATOR_SUCCESSFULLY_TITLE,
                message: MOVED_INDICATOR_SUCCESSFULLY_MESSAGE,
                type: MessageType.Success
            }
        });
        yield put({
            type: masterDataActions.getMasterData.type,
            payload: { pageNumber: 1, pageSize: 20, searchText: '' }
        });
    }
};

const moveIndicatorWatch = function* () {
    yield takeLatest(masterDataActions.moveIndicatorBegin.type, moveIndicator);
};

const getDeleteFeasibility = function* ({ payload }: PayloadAction<any>) {
    const { hasErrors, errors, value }: ApiResult<any> =
        yield MasterDataService.getDeleteFeasibility(payload);
    if (hasErrors && errors) {
        yield put({
            type: masterDataActions.getDeleteFeasibilityFailed.type,
            payload: value && value.detail
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value && value.detail,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: masterDataActions.getDeleteFeasibilitySuccess.type,
            payload: value
        });
    }
};

const getDeleteFeasibilityWatch = function* () {
    yield takeLatest(masterDataActions.getDeleteFeasibility.type, getDeleteFeasibility);
};

const getArchiveFeasibility = function* ({
    payload
}: PayloadAction<{ id: string; hierarchyId: string; entityType: EntityType }>) {
    const { hasErrors, errors, value } = yield MasterDataService.getArchiveFeasibility(payload);
    if (hasErrors && errors) {
        yield put({
            type: masterDataActions.getArchiveFeasibilityFailed.type,
            payload: value && value.detail
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value && value.detail,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: masterDataActions.getArchiveFeasibilitySuccess.type,
            payload: value
        });
    }
};

const getArchiveFeasibilityWatch = function* () {
    yield takeLatest(masterDataActions.getArchiveFeasibility.type, getArchiveFeasibility);
};

const getChildSubCategoryData = function* ({ payload }: PayloadAction<any>) {
    yield put({
        type: appStateActions.showLoader.type,
        payload: true
    });
    let categories: ApiResult<any> = {
        errors: [],
        hasErrors: false
    };
    if (payload.entityType === EntityType.Category)
        categories = yield MasterDataService.getEntitiesByCategoryId(payload.id);
    else categories = yield MasterDataService.getEntitiesByHierarchyId(payload.hierarchyId);

    if (categories && categories.hasErrors && categories.errors) {
        yield put({
            type: masterDataActions.getChildSubCategoryDataFailed.type,
            payload: categories.errors
        });
    } else {
        let isSubCatExist = false;
        if (categories?.value && categories?.value?.length > 0) {
            categories?.value.map((item: any, index: any) => {
                if (item.name == payload.sourceItemName) {
                    isSubCatExist = true;
                }
            });
            if (isSubCatExist == false) {
                yield put({
                    type: masterDataActions.getChildSubCategoryDataSuccess.type,
                    payload: false
                });
            } else {
                yield put({
                    type: masterDataActions.getChildSubCategoryDataSuccess.type,
                    payload: true
                });

                yield put({
                    type: appStateActions.showMessageModal.type,
                    payload: {
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message:
                            '"' +
                            payload.sourceItemName +
                            '" exists under this ' +
                            payload.entityType.toLowerCase() +
                            ' already !! Please choose a different target.',
                        type: MessageType.Error
                    }
                });
            }
        } else {
            yield put({
                type: masterDataActions.getChildSubCategoryDataSuccess.type,
                payload: false
            });
        }
    }

    yield put({
        type: appStateActions.showLoader.type,
        payload: false
    });
};

const getChildSubCategoryDataWatch = function* () {
    yield takeLatest(masterDataActions.getChildSubCategoryDataBegin.type, getChildSubCategoryData);
};

const isMovableSubCategory = function* ({ payload }: PayloadAction<any>) {
    const { hasErrors, errors, value }: ApiResult<any> =
        yield MasterDataService.getIsMovableSubCategory(payload);
    if (hasErrors && errors) {
        yield put({
            type: masterDataActions.isMovableSubCategoryError.type,
            payload: value && value.detail
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value && value.detail,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: masterDataActions.isMovableSubCategorySuccess.type,
            payload: value
        });
    }
};

const isMovableSubCategoryWatch = function* () {
    yield takeLatest(masterDataActions.isMovableSubCategoryBegin.type, isMovableSubCategory);
};

const discardChanges = function* () {
    const { hasErrors, errors, value } = yield MasterDataService.discardChanges();
    if (hasErrors && errors) {
        yield put({
            type: masterDataActions.discardChangesFailed.type,
            payload: value && value.detail
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value && value.detail,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: masterDataActions.discardChangesSuccess.type
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: DISCARD_CHANGES_SUCCESS,
                type: MessageType.Success
            }
        });
        yield put({
            type: masterDataActions.getMasterData.type,
            payload: { pageNumber: 1, pageSize: 20, searchText: '' }
        });
    }
};

const discardChangesWatch = function* () {
    yield takeLatest(masterDataActions.discardChanges.type, discardChanges);
};

const getCloneFeasibility = function* ({
    payload
}: PayloadAction<{ id: string; entityType: EntityType }>) {
    const { hasErrors, errors, value } = yield MasterDataService.getCloneFeasibility(payload);
    if (hasErrors && errors) {
        yield put({
            type: masterDataActions.getCloneFeasibilityFailed.type,
            payload: value && value.detail
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value?.detail || DEFAULT_ERROR_MESSAGE,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: masterDataActions.getCloneFeasibilitySuccess.type,
            payload: value
        });
    }
};

const getCloneFeasibilityWatch = function* () {
    yield takeLatest(masterDataActions.getCloneFeasibility.type, getCloneFeasibility);
};

const checkDuplicateIndicator = function* ({ payload }: PayloadAction<string>) {
    const { hasErrors, errors, value }: ApiResult<any> =
        yield MasterDataService.checkDuplicateIndicator(payload);
    if (hasErrors && errors) {
        yield put({
            type: masterDataActions.checkDuplicateIndicatorFailed.type,
            payload: value && value.detail
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: value && value.detail,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: masterDataActions.checkDuplicateIndicatorSuccess.type,
            payload: value
        });
    }
};

const checkDuplicateIndicatorWatch = function* () {
    yield takeLatest(masterDataActions.checkDuplicateIndicator.type, checkDuplicateIndicator);
};

export {
    getMasterDataWatch,
    getEntitiesByCategoryWatch,
    updateCategoryWatch,
    updateSubCategoryWatch,
    updateIndicatorWatch,
    deleteDataWatch,
    getSearchListWatch,
    addCategoryWatch,
    saveToMasterWatch,
    applyChangesWatch,
    addIndicatorWatch,
    linkIndicatorWatch,
    archiveWatch,
    cloneEntityWatch,
    getHierarchicalDataWatch,
    getHierarchicalDataByCategoryWatch,
    addSubCategoryWatch,
    fetchSubCategoryWatch,
    fetchReviewStatusWatch,
    refreshEntitiesByCategoryWatch,
    moveSubCategoryWatch,
    copyIndicatorWatch,
    resetMasterDataStateWatch,
    moveIndicatorWatch,
    getDeleteFeasibilityWatch,
    getArchiveFeasibilityWatch,
    getChildSubCategoryDataWatch,
    isMovableSubCategoryWatch,
    discardChangesWatch,
    getCloneFeasibilityWatch,
    checkDuplicateIndicatorWatch
};
